// PatientDashboard.js
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Alert, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FaCalendarAlt,
  FaFilePrescription,
  FaFlask,
} from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import demoImage from "../../assets/images/patientbed.png"; // replace with your png/svg

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PatientDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [labResults, setLabResults] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [user, setUser] = useState({});
  const patientId = localStorage.getItem("patient_id");
    const navigate = useNavigate();

  useEffect(() => {
    // Add an extra entry to browser history
    window.history.pushState(null, "", window.location.href);

    const handleBack = () => {
      window.history.go(1); // prevent back
    };

    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, []);

  useEffect(() => {
    const userObj = JSON.parse(localStorage.getItem("user"));
    setUser(userObj || {});

    if (patientId) {
      axios
        .get(`${BASE_URL}/api/patient/appointments/${patientId}`)
        .then((res) => setAppointments(res.data || []))
        .catch(() => setAppointments([]));

      axios
        .get(`${BASE_URL}/api/patient/prescriptions/${patientId}`)
        .then((res) => setPrescriptions(res.data || []))
        .catch(() => setPrescriptions([]));

      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      axios
        .get(`${BASE_URL}/api/lab/lab-results/${patientId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        .then((res) => setLabResults(res.data || []))
        .catch(() => setLabResults([]));
    }
  }, [patientId]);

  useEffect(() => {
    let latestAppointment = null;
    let latestPrescription = null;
    let latestLabResult = null;

    if (appointments.length > 0) {
      latestAppointment = [...appointments].sort(
        (a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate)
      )[0];
    }
    if (prescriptions.length > 0) {
      latestPrescription = [...prescriptions].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      )[0];
    }
    if (labResults.length > 0) {
      latestLabResult = [...labResults].sort(
        (a, b) =>
          new Date(b.requested_at || b.created_at || 0) -
          new Date(a.requested_at || a.created_at || 0)
      )[0];
    }

    const notificationsArr = [];
    if (latestAppointment) {
      notificationsArr.push({
        id: 1,
        type: "appointment",
        message: `Your appointment on ${latestAppointment.appointmentDate} is ${
          latestAppointment.status
            ? latestAppointment.status.charAt(0).toUpperCase() +
              latestAppointment.status.slice(1)
            : "Scheduled"
        }.`,
      });
    }
    if (latestPrescription) {
      const prescDate = latestPrescription.created_at
        ? latestPrescription.created_at.split("T")[0]
        : "";
      notificationsArr.push({
        id: 2,
        type: "prescription",
        message: `Prescription dated ${prescDate} is available.`,
      });
    }
    if (latestLabResult) {
      const labDate = latestLabResult.requested_at
        ? new Date(latestLabResult.requested_at).toLocaleDateString()
        : "";
      notificationsArr.push({
        id: 3,
        type: "lab",
        message: `Lab report for "${
          latestLabResult.test_type || "Test"
        }" (${labDate}) is ${
          latestLabResult.status ? latestLabResult.status : "Available"
        }.`,
      });
    }
    setNotifications(notificationsArr);
  }, [appointments, prescriptions, labResults]);

  return (
    <div className="dashboard-container p-4" style={{ background: "#f8f9fa" }}>
      {/* Header */}
      <Card className="dashboard-header p-4 shadow-sm mb-4">
        <Row className="align-items-center">
          <Col md={8}>
            <h5 className="mb-1">Good Morning,</h5>
            <h2 className="fw-bold welcome-text">
              Welcome, {user?.name || "Patient"}!
            </h2>
            <p className="text-muted">
              Your health, your records, your appointments – all in one place.
            </p>
          </Col>
          <Col md={4} className="text-center">
            <img src={demoImage} alt="Dashboard" className="header-image" />
          </Col>
        </Row>
      </Card>

      {/* Stat Cards */}
 <Row className="g-4 mb-4">
  {/* Appointment Card */}
  <Col md={4}>
    <Card className="stat-card shadow-sm h-100">
      <Card.Body className="d-flex align-items-center">
        {/* Left Vertical Line */}
        <div className="vertical-line me-3"></div>

        {/* Content */}
        <div className="flex-grow-1">
          <div className="d-flex justify-content-between align-items-start">
            <span className="stat-count">{appointments.length}</span>
            <FaCalendarAlt className="stat-icon" />
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <h6 className="mb-0 fw-bold">Appointment</h6>
            <Link to="/patient_appointment" className="stat-arrow">
              ↗
            </Link>
          </div>
        </div>
      </Card.Body>
    </Card>
  </Col>

  {/* Prescription Card */}
  <Col md={4}>
    <Card className="stat-card shadow-sm h-100">
      <Card.Body className="d-flex align-items-center">
        <div className="vertical-line me-3"></div>

        <div className="flex-grow-1">
          <div className="d-flex justify-content-between align-items-start">
            <span className="stat-count">
              {prescriptions.length.toString().padStart(2, "0")}
            </span>
            <FaFilePrescription className="stat-icon" />
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <h6 className="mb-0 fw-bold">Prescription</h6>
            <Link to="/prescription" className="stat-arrow">
              ↗
            </Link>
          </div>
        </div>
      </Card.Body>
    </Card>
  </Col>

  {/* Lab Results Card */}
  <Col md={4}>
    <Card className="stat-card shadow-sm h-100">
      <Card.Body className="d-flex align-items-center">
        <div className="vertical-line me-3"></div>

        <div className="flex-grow-1">
          <div className="d-flex justify-content-between align-items-start">
            <span className="stat-count">{labResults.length}</span>
            <FaFlask className="stat-icon" />
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <h6 className="mb-0 fw-bold">Lab Results</h6>
            <Link to="/patient_report" className="stat-arrow">
              ↗
            </Link>
          </div>
        </div>
      </Card.Body>
    </Card>
  </Col>
</Row>


      {/* Notifications */}
      <Card className="p-3 shadow-sm">
        {notifications.length > 0 ? (
          notifications.map((n) => (
            <Alert
              key={n.id}
              variant="info"
              className="mb-2"
              style={{
                borderRadius: 8,
                fontWeight: 500,
                background: "#f8f9fa",
                color: "var(--primary-color)",
                border: "1px solid #e0f7fa",
                display: "flex",
                alignItems: "center",
              }}
            >
              {n.type === "appointment" ? (
                <FaCalendarAlt className="me-2 primary-class" />
              ) : n.type === "prescription" ? (
                <FaFilePrescription className="me-2 primary-class" />
              ) : (
                <FaFlask className="me-2 primary-class" />
              )}
              {n.message}
            </Alert>
          ))
        ) : (
          <div >No notifications available.</div>
        )}
      </Card>
    </div>
  );
};

export default PatientDashboard;
